<template>
  <div
    v-if="location"
    class="mb-4"
  >
    <smpw-map
      ref="googleMapComponent"
      :center="mapCenter"
      @center_changed="updateCenter"
    >
      <smpw-map-info-window
        :selected-marker="selectedMarker"
        :location="location"
      />
      <smpw-map-marker
        v-if="store"
        :title="store.name"
        :position="{ lat: store.lat, lng: store.lng } "
        :read-only="true"
        @click="toggleInfoWindow(store, -5)"
      />
      <smpw-map-marker
        v-for="(m, index) in markersWithNumbers"
        :key="index"
        :title="m.marker.description"
        :position="m.marker.position"
        :color="m?.type?.color"
        :icon="m?.number.toString()"
        :read-only="readOnly"
        @update:position="updateLocation($event, m.marker)"
        @click="toggleInfoWindow(m, index)"
      />
    </smpw-map>

    <!-- Location Markers -->
    <div
      v-for="(markerType, index) in markerTypes"
      :key="index"
      class="my-2"
    >
      <v-card
        v-if="!readOnly || (getMarkersByType(markerType).length > 0)"
        class="mb-4"
      >
        <v-toolbar
          :color="markerType.color"
          class="mb-3 text-white"
        >
          <v-icon
            :icon="`fas fa-${markerType.icon}`"
            class="ms-3 me-4"
          />
          <span>{{ markerType.name }}</span>
          <v-spacer />
          <v-card-actions v-if="!readOnly">
            <v-btn
              size="small"
              color="white"
              class="text-font"
              :text="t('location.add_new', { type: markerType.name })"
              @click="addNewMarker(markerType)"
            />
          </v-card-actions>
        </v-toolbar>

        <div
          v-for="(marker, markerIndex) in getMarkersByType(markerType)"
          :key="markerIndex"
          class="d-flex flex-row justify-space-between align-center my-2"
        >
          <div class="d-flex flex-row align-center flex-grow-1">
            <div class="ms-2">
              <div
                v-if="readOnly"
                class="lh-1 mb-1 font-weight-bold text-font"
              >
                {{ markerType.name }} <span v-if="markerType.max > 1">#{{ (markerIndex + 1) }}</span>
              </div>
              <div
                v-if="readOnly"
                class="lh-1 text-font"
              >
                {{ marker.description }}
              </div>
            </div>

            <v-text-field
              v-if="!readOnly"
              v-model="marker.description"
              width="100%"
              class="mt-1 me-1 flex-grow-1"
              :label="`${markerType.name} ${(markerType.max > 1) ? `#${(index + 1)}` : ''} ${t('global.description')}:`"
              hide-details
            />
          </div>
          <div
            v-if="!readOnly"
            class="d-flex flex-column flex-sm-row align-center"
          >
            <localizer-dialog
              v-if="!readOnly"
              class="me-1"
              :class="smAndDown ? 'mb-2' : ''"
              :color="markerType.color"
              :icon="(index + 1).toString()"
              @update:location="updateLocationFromLocalizer($event, marker)"
            />
            <image-selector
              v-model="marker.images"
              class="me-1"
              :class="smAndDown ? 'mb-2' : ''"
              :images="location.images"
            />
            <v-btn
              v-if="!readOnly"
              color="error"
              class="me-1"
              :class="smAndDown ? 'mb-2' : ''"
              size="small"
              @click="deleteItem(marker.uid)"
            >
              <v-icon icon="fas fa-trash" />
            </v-btn>
          </div>
          <div
            v-if="readOnly"
          >
            <v-btn
              v-if="marker.position?.lat && marker.position?.lng"
              color="primary"
              :href="getDirectionsUrl(marker.position.lat, marker.position.lng)"
              target="_blank"
              size="small"
              prepend-icon="fas fa-compass"
              :text="t('global.directions')"
              class="me-2 float-right mb-2"
            />
            <image-slideshow
              v-if="marker.images && marker.images.length > 0"
              :images="marker.images?.map((image) => ({ ...image, href: image.signed_file_url }))"
              as-dialog
              button-color="primary-lighten-1"
              class="me-2 float-right mb-2"
            />
          </div>
        </div>
      </v-card>
    </div>

    <v-card v-if="store">
      <v-toolbar
        color="primary"
      >
        <v-icon
          icon="fas fa-warehouse"
          class="mx-5"
        />
        <span>{{ t('store.title') }}</span>
      </v-toolbar>
      <div class="d-flex flex-row justify-space-between align-center my-2">
        <div class="d-flex flex-row align-center flex-grow-1">
          <div class="font-weight-bold ms-2 text-font">
            {{ store.name }}
          </div>
        </div>
        <div class="d-flex flex-column flex-sm-row align-start">
          <v-btn
            :to="`/stores/${store.id}`"
            :text="t('global.details')"
            color="primary"
            :class="{ 'mt-2': smAndDown }"
            class="me-1"
            size="small"
          />
          <v-btn
            :href="getDirectionsUrl(store.lat, store.lng)"
            target="_blank"
            size="small"
            prepend-icon="fas fa-compass"
            :text="t('global.directions')"
            color="primary"
            :class="{ 'mt-2': smAndDown }"
            class="me-1"
          />
          <image-slideshow
            v-if="storeSlideshowImages.length > 0"
            :as-dialog="true"
            button-color="primary"
            font-color="white"
            class="me-1"
            :class="{ 'mt-2': smAndDown }"
            :images="storeSlideshowImages"
          />
        </div>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import debounce from 'lodash/debounce';
import { storeToRefs } from 'pinia';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import SmpwMap from '@/components/globals/SmpwMap.vue';
import SmpwMapMarker from '@/components/globals/SmpwMapMarker.vue';
import { useErrorStore } from '@/stores/errors';
import { useMapStore } from '@/stores/maps';
import LocalizerDialog from '../dialogs/LocalizerDialog.vue';
import SmpwMapInfoWindow from '../globals/SmpwMapInfoWindow.vue';
import ImageSlideshow from '../images/ImageSlideshow.vue';
import ImageSelector from './ImageSelector.vue';

// Need to set  store correctly.
const props = defineProps<{
  name?: string;
  store?: App.Models.Store;
  readOnly?: boolean;
}>();
const selectedMarker = ref();
const { t } = useI18n();
const mapStore = useMapStore();

const { getDirectionsUrl } = useMapStore();
const {
  cityCentre,
  infoWindowOpen,
  currentInfoWinMarker,
  infoOptions,
} = storeToRefs(mapStore);

const { showSnackMessage, handleError } = useErrorStore();
const { smAndDown } = useDisplay();
const location = defineModel<App.Models.Location>();
const markerTypes = ref<Array<App.Models.LocationMarkerType>>([]);
function updateLocation(e: google.maps.MapMouseEvent, marker: App.Models.LocationMarker): void {
  if (!e.latLng) {
    return;
  }
  marker.lat = e.latLng.lat();
  marker.lng = e.latLng.lng();
  marker.position = {
    lat: e.latLng.lat(),
    lng: e.latLng.lng(),
  };
}

const googleMapComponent = ref<any>();

/** The store's images in the shape the slideshow expects; empty when there is no store. */
const storeSlideshowImages = computed(() =>
  (props.store?.images ?? []).map((image) => ({ href: image.signed_file_url })));

const mapCenter = ref<google.maps.LatLngLiteral>({ ...cityCentre.value });

async function executeAutoZoom() {
  await new Promise(r => setTimeout(r, 500));
  if (!googleMapComponent.value) {
    return;
  }
  const bounds = new google.maps.LatLngBounds();

  let boundsSet = false;
  if (props.store) {
    bounds.extend({ lat: props.store.lat, lng: props.store.lng });
    boundsSet = true;
  }

  location.value?.markers?.filter((marker) => !marker.deleted).forEach((m) => {
    bounds.extend({
      lat: Number(m.position.lat),
      lng: Number(m.position.lng),
    });
    boundsSet = true;
  });

  if (boundsSet) {
    googleMapComponent.value.mapRef.map.fitBounds(bounds);
  }
}

function getMarkersByType(markerType: App.Models.LocationMarkerType): App.Models.LocationMarker[] {
  if (!location.value || !location.value.markers) {
    return [];
  }
  return location.value.markers.filter((marker) => (marker.marker_type_id === markerType.id) && !marker.deleted).sort((a, b) => a.order - b.order);
}

/** A marker paired with its type and its 1-based position within that type. */
interface NumberedMarker {
  marker: App.Models.LocationMarker;
  type: App.Models.LocationMarkerType;
  number: number;
}

const markersWithNumbers = computed<NumberedMarker[]>(() => {
  const sortedMarkers: NumberedMarker[] = [];
  markerTypes.value.forEach((markerType) => {
    getMarkersByType(markerType).forEach((marker, index) => {
      sortedMarkers.push({
        marker,
        type: markerType,
        number: (index + 1),
      });
    });
  });
  return sortedMarkers;
});

function getUniqueMarkerUid(): number {
  if (location.value?.markers?.length === 0) {
    return 1;
  }
  const maxUid = location.value?.markers?.filter((m) => !Number.isNaN(m.uid)).map((m) => Number(m.uid ?? 0)) ?? [];
  return Math.max(0, ...maxUid) + 1;
}

onMounted(() => {
  location.value?.markers?.forEach((marker) => {
    marker.uid = getUniqueMarkerUid();
    marker.deleted = marker.deleted ?? false;
  });
  executeAutoZoom();
});

function updateMarkerOrderValues() {
  markerTypes.value.forEach((markerType) => {
    getMarkersByType(markerType).forEach((marker, index) => {
      marker.order = index;
    });
  });
}

async function toggleInfoWindow(marker: any, idx: number): Promise<void> {
  // Check type of IDX, filter through.
  const isLocation = !!marker?.marker?.location_id;
  const markerPosition = isLocation
    ? marker?.marker?.position
    : { lat: marker.lat, lng: marker.lng };

  if (currentInfoWinMarker.value === idx) {
    infoWindowOpen.value = !infoWindowOpen.value;
  }
  else {
    infoOptions.value = {
      position: markerPosition,
      pixelOffset: {
        width: 0,
        height: -35,
      },
    };
    selectedMarker.value = marker;
    currentInfoWinMarker.value = idx;
    infoWindowOpen.value = true;
  }
}

const updateCenter = debounce((e) => {
  if (e) {
    mapCenter.value = e;
  }
}, 500);

function updateLocationFromLocalizer(e: Position, marker: App.Models.LocationMarker): void {
  marker.position = e;
}

function addNewMarker(markerType: App.Models.LocationMarkerType): void {
  if (!location.value) {
    return;
  }
  if (!location.value?.markers) {
    location.value.markers = [];
  }
  const markerCount = getMarkersByType(markerType).length;
  const maxMarkerCount = markerType.max;
  if (markerCount < maxMarkerCount) {
    const marker: Partial<App.Models.LocationMarker> = {
      id: null,
      uid: getUniqueMarkerUid(),
      marker_type_id: markerType.id ?? 0,
      // Appended after the existing markers of this type. Without it the marker enters
      // `getMarkersByType`'s sort with an undefined order, which compares as NaN.
      order: markerCount,
      images: [],
      description: `${markerType.name} ${markerCount + 1}`,
      position: { lat: mapCenter.value.lat, lng: mapCenter.value.lng },
      deleted: false,
      location_id: location.value.id,
    };
    // TODO: check this type casting, a considerable amount of properties are missing
    location.value?.markers.push(marker as App.Models.LocationMarker);
    updateMarkerOrderValues();
  }
  else {
    showSnackMessage(t('location.too_many_markers', { marker: markerType.name, max_count: maxMarkerCount }));
  }
}

function deleteItem(uid: string | number | undefined): void {
  if (location.value?.markers) {
    const markerIndex = location.value?.markers.findIndex((marker) => marker.uid === uid);
    if (location.value?.markers[markerIndex]) {
      location.value.markers[markerIndex].deleted = true;
    }
  }
  updateMarkerOrderValues();
}

async function loadMarkerTypes() {
  try {
    const response = await axios.get('/api/locations/markers');
    markerTypes.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('location.cannot_load_types'));
  }
}

loadMarkerTypes();
</script>
